/**
 * Billing (parent app) — calm balance card, upcoming invoice, history.
 *
 * Mobile-friendly first. Reuses the existing parent-app design language
 * (sage greens, Fraunces display, Plus Jakarta body). No new colors.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Receipt, Wallet, Clock, ExternalLink, CreditCard } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";

function money(amount, currency = "AED") {
  return `${currency} ${Number(amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function StatusPill({ status }) {
  const { t } = useI18n();
  const palette = {
    paid: "bg-[#E6EFE8] text-[#1F3A2A]",
    issued: "bg-[#F4E5C5] text-[#8C6D1F]",
    pending: "bg-[#FDE8C5] text-[#8C4A1F]",
    partial: "bg-[#F4E5C5] text-[#8C6D1F]",
    overdue: "bg-[#FCE2E2] text-[#A11D1D]",
    refunded: "bg-[#EEF0F4] text-[#6B7280]",
    adjusted: "bg-[#EEF0F4] text-[#6B7280]",
    void: "bg-[#EEF0F4] text-[#9CA3AF] line-through",
  };
  return (
    <span
      className={`inline-block text-[10px] tracking-[0.1em] uppercase font-semibold rounded-full px-2 py-0.5 ${palette[status] || palette.issued}`}
    >
      {t(`billing.status.${status}`) || status}
    </span>
  );
}

export default function ParentBilling() {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payOpen, setPayOpen] = useState(null); // selected invoice (offline modal)
  const [payForm, setPayForm] = useState({ amount: "", method: "bank_transfer", reference: "", note: "" });
  const [stripeBusy, setStripeBusy] = useState(null); // invoice_id being charged
  const [stripePolling, setStripePolling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, i, p] = await Promise.all([
        api.get("/billing/me"),
        api.get("/billing/me/invoices"),
        api.get("/billing/me/payments"),
      ]);
      setSummary(s.data);
      setInvoices(i.data || []);
      setPayments(p.data || []);
    } catch (e) {
      // ignore
    }
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  const openPay = (inv) => {
    setPayForm({
      amount: String(inv.amount_due || ""),
      method: "bank_transfer",
      reference: "",
      note: "",
    });
    setPayOpen(inv);
  };

  const submitPay = async () => {
    try {
      await api.post(`/billing/me/invoices/${payOpen.id}/pay-now`, {
        amount: Number(payForm.amount),
        method: payForm.method,
        reference: payForm.reference || null,
        note: payForm.note || null,
      });
      toast.success(t("billing.parent.pay_now_success"));
      setPayOpen(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("billing.errors.generic"));
    }
  };

  const startStripeCheckout = async (inv) => {
    setStripeBusy(inv.id);
    try {
      const r = await api.post(`/billing/me/invoices/${inv.id}/stripe-checkout`, {
        origin_url: window.location.origin,
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
        return;
      }
      toast.error(t("billing.errors.generic"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("billing.errors.generic"));
    } finally {
      setStripeBusy(null);
    }
  };

  // Post-return polling: when the parent comes back from Stripe Checkout
  // with ?stripe_session_id=… we poll the status endpoint for up to ~30s.
  useEffect(() => {
    const sessionId = searchParams.get("stripe_session_id");
    if (!sessionId || stripePolling) return;
    setStripePolling(true);
    let attempts = 0;
    const maxAttempts = 10;
    const interval = 2500;
    const tick = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/billing/payments/stripe/status/${sessionId}`);
        if (r.data?.payment_status === "paid") {
          toast.success(t("billing.parent.stripe_payment_success"));
          setSearchParams({});
          setStripePolling(false);
          load();
          return;
        }
        if (r.data?.payment_status === "canceled" || r.data?.status === "expired") {
          toast.error(t("billing.parent.stripe_payment_canceled"));
          setSearchParams({});
          setStripePolling(false);
          return;
        }
      } catch (_e) {
        // keep polling
      }
      if (attempts < maxAttempts) {
        window.setTimeout(tick, interval);
      } else {
        toast.message(t("billing.parent.stripe_payment_pending"));
        setSearchParams({});
        setStripePolling(false);
        load();
      }
    };
    tick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const openInvoices = useMemo(
    () => invoices.filter((i) => !["paid", "void", "refunded"].includes(i.status)),
    [invoices]
  );

  if (loading) {
    return (
      <main className="px-5 py-8 max-w-2xl mx-auto">
        <p className="font-serif italic text-[#525252]">{t("billing.common.loading")}</p>
      </main>
    );
  }

  const currency = summary?.currency || "AED";
  const outstanding = summary?.total_outstanding || 0;
  const upcoming = summary?.upcoming_invoice;

  return (
    <main className="pb-24 pt-6 max-w-2xl mx-auto px-5 space-y-6" data-testid="parent-billing">
      <header>
        <span className="eyebrow">{t("billing.parent.eyebrow")}</span>
        <h1 className="mt-2 font-display text-3xl text-[#1A1B2E]">
          {t("billing.parent.title_lead")}{" "}
          <em className="font-serif italic text-[#1F3A2A]">{t("billing.parent.title_italic")}</em>
        </h1>
      </header>

      <section
        className="rounded-3xl bg-[#1F3A2A] text-white p-6 lg:p-8 relative overflow-hidden"
        data-testid="parent-billing-balance-card"
      >
        <div className="flex items-start gap-3">
          <Wallet size={20} strokeWidth={1.4} className="opacity-70" />
          <div>
            <div className="text-xs tracking-[0.1em] uppercase font-medium opacity-70">
              {t("billing.parent.balance_outstanding")}
            </div>
            <div
              className="mt-2 font-display text-4xl tabular-nums"
              data-testid="parent-billing-balance-amount"
            >
              {money(outstanding, currency)}
            </div>
          </div>
        </div>
        {summary?.next_due_at && outstanding > 0 ? (
          <div className="mt-4 text-sm opacity-80">
            {t("billing.parent.next_due")}: <span className="font-medium">{summary.next_due_at}</span>
          </div>
        ) : outstanding <= 0 ? (
          <p className="mt-4 font-serif italic opacity-80">{t("billing.parent.no_balance")}</p>
        ) : null}
        {upcoming && (
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              onClick={() => startStripeCheckout(upcoming)}
              disabled={stripeBusy === upcoming.id}
              data-testid="parent-billing-pay-with-card"
              className="inline-flex items-center gap-2 rounded-full bg-white text-[#1F3A2A] text-sm font-medium px-4 py-2 hover:bg-white/90 disabled:opacity-60"
            >
              <CreditCard size={14} strokeWidth={1.6} />
              {stripeBusy === upcoming.id
                ? t("billing.parent.stripe_redirecting")
                : t("billing.parent.pay_with_card_cta")}
            </button>
            <button
              onClick={() => openPay(upcoming)}
              data-testid="parent-billing-pay-now"
              className="inline-flex items-center gap-2 rounded-full bg-white/15 text-white text-sm font-medium px-4 py-2 hover:bg-white/25 border border-white/20"
            >
              {t("billing.parent.pay_now_cta")}
            </button>
          </div>
        )}
      </section>

      {openInvoices.length > 0 && (
        <section data-testid="parent-billing-open-invoices">
          <h2 className="eyebrow">Open invoices</h2>
          <ul className="mt-3 space-y-3">
            {openInvoices.map((inv) => (
              <li
                key={inv.id}
                data-testid={`parent-billing-invoice-${inv.id}`}
                className="rounded-2xl bg-white border border-[#EEF0F4] p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium text-[#1A1B2E]">{inv.invoice_number}</div>
                    <div className="text-xs text-[#6B7280] mt-0.5">
                      {t("billing.parent.child_label")} {inv.child_name} · due {inv.due_at || "—"}
                    </div>
                    {inv.status === "pending" && (
                      <p className="mt-2 text-xs font-serif italic text-[#8C4A1F]">
                        {t("billing.parent.invoice_status_pending")}
                      </p>
                    )}
                    {inv.status === "overdue" && (
                      <p className="mt-2 text-xs font-serif italic text-[#A11D1D]">
                        {t("billing.parent.invoice_status_overdue")}
                      </p>
                    )}
                    {inv.status === "partial" && (
                      <p className="mt-2 text-xs font-serif italic text-[#8C6D1F]">
                        {t("billing.parent.invoice_status_partial")}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="font-display text-base text-[#1A1B2E] tabular-nums">
                      {money(inv.amount_due, inv.currency)}
                    </div>
                    <StatusPill status={inv.status} />
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <a
                    href={`/billing/invoices/${inv.id}/print`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`parent-billing-view-${inv.id}`}
                    className="text-xs text-[#1F3A2A] hover:underline inline-flex items-center gap-1"
                  >
                    <ExternalLink size={12} strokeWidth={1.6} /> view
                  </a>
                  {!["paid", "void", "refunded"].includes(inv.status) && (
                    <>
                      <button
                        onClick={() => startStripeCheckout(inv)}
                        disabled={stripeBusy === inv.id}
                        data-testid={`parent-billing-pay-card-${inv.id}`}
                        className="text-xs text-[#1F3A2A] hover:underline font-medium inline-flex items-center gap-1 disabled:opacity-60"
                      >
                        <CreditCard size={11} strokeWidth={1.6} />
                        {stripeBusy === inv.id
                          ? t("billing.parent.stripe_redirecting")
                          : t("billing.parent.pay_with_card_cta")}
                      </button>
                      <button
                        onClick={() => openPay(inv)}
                        data-testid={`parent-billing-pay-${inv.id}`}
                        className="text-xs text-[#525252] hover:underline"
                      >
                        {t("billing.parent.pay_offline_cta")}
                      </button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h2 className="eyebrow">{t("billing.parent.history_title")}</h2>
        {payments.length === 0 ? (
          <p className="mt-3 font-serif italic text-[#9CA3AF]" data-testid="parent-billing-history-empty">
            {t("billing.parent.history_empty")}
          </p>
        ) : (
          <ul className="mt-3 space-y-3">
            {payments.map((p) => (
              <li
                key={p.id}
                data-testid={`parent-billing-payment-${p.id}`}
                className="flex items-center justify-between rounded-2xl bg-white border border-[#EEF0F4] px-4 py-3"
              >
                <div>
                  <div className="text-sm text-[#1A1B2E]">
                    {money(p.amount, p.currency)} · {t(`billing.method.${p.method}`)}
                  </div>
                  <div className="text-xs text-[#6B7280]">
                    {p.received_at?.slice(0, 10)} · invoice {p.invoice_number}
                  </div>
                </div>
                <StatusPill status={p.status} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section
        data-testid="parent-billing-autopay-section"
        className="rounded-2xl bg-[#FAF9F1] border border-[#EEF0F4] p-4"
      >
        <div className="flex items-center gap-2">
          <Clock size={14} strokeWidth={1.6} className="text-[#1F3A2A]" />
          <span className="text-sm font-medium text-[#1A1B2E]">{t("billing.parent.autopay_section")}</span>
        </div>
        <p className="text-xs text-[#6B7280] mt-1 font-serif italic">
          {t("billing.parent.autopay_coming_soon")}
        </p>
      </section>

      {payOpen && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="parent-billing-pay-modal"
          onClick={() => setPayOpen(null)}
          className="fixed inset-0 z-50 grid place-items-end sm:place-items-center bg-black/40 p-0 sm:p-4"
        >
          <div
            className="bg-white rounded-t-3xl sm:rounded-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-display text-xl text-[#1A1B2E]">
              {t("billing.parent.pay_now_modal_title")}
            </h3>
            <p className="mt-1 text-sm text-[#6B7280]">
              {t("billing.parent.pay_now_offline_disclaimer")}
            </p>
            <div className="mt-5 space-y-3">
              <label className="block">
                <span className="eyebrow">{t("billing.parent.pay_now_amount")}</span>
                <input
                  type="number"
                  step="0.01"
                  data-testid="parent-billing-pay-amount"
                  value={payForm.amount}
                  onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm focus:outline-none focus:border-[#1F3A2A]"
                />
              </label>
              <label className="block">
                <span className="eyebrow">{t("billing.parent.pay_now_method")}</span>
                <select
                  value={payForm.method}
                  onChange={(e) => setPayForm({ ...payForm, method: e.target.value })}
                  data-testid="parent-billing-pay-method"
                  className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm bg-white focus:outline-none focus:border-[#1F3A2A]"
                >
                  <option value="bank_transfer">{t("billing.method.bank_transfer")}</option>
                  <option value="cash">{t("billing.method.cash")}</option>
                  <option value="check">{t("billing.method.check")}</option>
                  <option value="card_offline">{t("billing.method.card_offline")}</option>
                  <option value="other">{t("billing.method.other")}</option>
                </select>
              </label>
              <label className="block">
                <span className="eyebrow">{t("billing.parent.pay_now_reference")}</span>
                <input
                  type="text"
                  data-testid="parent-billing-pay-reference"
                  value={payForm.reference}
                  onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm focus:outline-none focus:border-[#1F3A2A]"
                />
              </label>
              <label className="block">
                <span className="eyebrow">{t("billing.parent.pay_now_note")}</span>
                <textarea
                  rows={2}
                  data-testid="parent-billing-pay-note"
                  value={payForm.note}
                  onChange={(e) => setPayForm({ ...payForm, note: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm focus:outline-none focus:border-[#1F3A2A]"
                />
              </label>
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button onClick={() => setPayOpen(null)} className="text-sm text-[#6B7280] hover:text-[#1A1B2E]">
                {t("billing.common.cancel")}
              </button>
              <button
                onClick={submitPay}
                data-testid="parent-billing-pay-submit"
                className="inline-flex items-center gap-2 rounded-full bg-[#1F3A2A] text-white text-sm font-medium px-4 py-2 hover:bg-[#132C17]"
              >
                {t("billing.parent.pay_now_submit")}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
